<template>
  <div>
    <b-container fluid>
      <b-row class="mt-3 mb-3">
        <b-col sm="12" md="10" offset-md="1" class="content-container">
          <b-row class="mt-3">
            <b-col cols="12">
              <h2>{{$t('myJobsTable.title')}}</h2>
            </b-col>
          </b-row>
          <b-row class="mt-3">
            <b-col sm="12" md="3" class="mb-1">
              <label for="job-name-filter-input">{{$t('myJobsTable.jobNameFilterLabel')}}</label>
              <b-form-input
                id="job-name-filter-input"
                :placeholder="$t('myJobsTable.jobNameFilterPlaceholder')"
                v-model="filter.jobName"
              ></b-form-input>
            </b-col>
            <b-col sm="12" md="3" class="mb-1">
              <label for="status-filter-input">{{$t('myJobsTable.jobStatusFilterLabel')}}</label>
              <b-form-select
                id="status-filter-input"
                v-model="filter.status"
              >
                <option
                  v-for="status in allowedStatuses"
                  :key="status.value"
                  :value="status.value">
                  {{status.label}}
                </option>
              </b-form-select>
            </b-col>
            <b-col sm="12" md="3" class="mb-1">
              <label for="from-date-filter-input">{{$t('myJobsTable.jobFromFilterLabel')}}</label>
              <b-form-datepicker
                id="from-date-filter-input"
                v-model="filter.fromDate"
                :placeholder="$t('myJobsTable.jobFromFilterPlaceholder')"
                reset-button
              ></b-form-datepicker>
            </b-col>
            <b-col sm="12" md="3" class="mb-1">
              <label for="to-date-filter-input">{{$t('myJobsTable.jobToFilterLabel')}}</label>
              <b-form-datepicker
                id="to-date-filter-input"
                v-model="filter.toDate"
                :placeholder="$t('myJobsTable.jobToFilterPlaceholder')"
                reset-button
              ></b-form-datepicker>
            </b-col>
          </b-row>
          <b-row class="mt-3" v-if="!showSimpleTable">
            <b-col cols="12">
              <b-table
                :fields="fields"
                :items="jobs(filter)"
                :current-page="currentPage"
                :per-page="perPage"
                :busy="loading"
                outlined
                show-empty
              >
                <template v-slot:table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong class="pl-2">{{$t('myJobsTable.tableLoading')}}</strong>
                  </div>
                </template>

                <template v-slot:empty="scope">
                  <p>{{$t('myJobsTable.tableEmpty')}}</p>
                </template>

                <template v-slot:head(name)>
                  {{$t('myJobsTable.tableJobName')}}
                </template>

                <template v-slot:head(inputLanguage)>
                  {{$t('myJobsTable.tableInputLanguage')}}
                </template>

                <template v-slot:head(desiredLanguage)>
                  {{$t('myJobsTable.tableDesiredLanguage')}}
                </template>

                <template v-slot:head(status)>
                  {{$t('myJobsTable.tableStatus')}}
                </template>

                <template v-slot:cell(status)="data">
                  <div
                    v-if="data.item.status === 'AwaitingProcessing'
                    || data.item.status === 'Processing'"
                  >
                    <b-badge variant="warning">
                      {{$t('myJobsTable.jobStatusProcessingLabel')}}
                    </b-badge>
                  </div>
                  <div v-if="data.item.status === 'Completed'">
                    <b-badge variant="success">
                      {{$t('myJobsTable.jobStatusCompletedLabel')}}
                    </b-badge>
                  </div>
                  <div v-if="data.item.status === 'Cancelled'">
                    <b-badge variant="danger">
                      {{$t('myJobsTable.jobStatusCancelledLabel')}}
                    </b-badge>
                  </div>
                  <div v-if="data.item.status === 'Failed'">
                    <b-badge variant="danger">
                      {{$t('myJobsTable.jobStatusFailedLabel')}}
                    </b-badge>
                  </div>
                </template>

                <template v-slot:head(actions)>
                  {{$t('myJobsTable.tableActions')}}
                </template>

                <template v-slot:cell(actions)="data">
                  <span
                    v-if="data.item.status === 'Completed'"
                    class="fas fa-external-link-alt hover hover-primary"
                    @click="viewJob(data.item.id)"
                  ></span>
                </template>
              </b-table>
            </b-col>
          </b-row>
          <b-row class="mt-3" v-else>
            <b-col cols="12">
              <b-table
                :fields="simpleFields"
                :items="jobs(filter)"
                :current-page="currentPage"
                :per-page="perPage"
                :busy="loading"
                outlined
                show-empty
                hover
              >
                <template v-slot:table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong class="pl-2">{{$t('myJobsTable.tableLoading')}}</strong>
                  </div>
                </template>

                <template v-slot:empty="scope">
                  <p>{{$t('myJobsTable.tableEmpty')}}</p>
                </template>

                <template v-slot:head(jobInfo)>
                  {{$t('myJobsTable.tableJobInfo')}}
                </template>

                <template v-slot:cell(jobInfo)="data">
                  <b-row @click="data.item.status === 'Completed' ? viewJob(data.item.id) : null">
                    <b-col cols="6">
                      <p class="mb-0">
                        {{data.item.name}}
                      </p>
                    </b-col>
                    <b-col cols="6">
                      <div
                        v-if="data.item.status === 'AwaitingProcessing'
                          || data.item.status === 'Processing'"
                      >
                        <b-badge variant="warning" class="float-right">
                          {{$t('myJobsTable.jobStatusProcessingLabel')}}
                        </b-badge>
                      </div>
                      <div v-if="data.item.status === 'Completed'" class="float-right">
                        <b-badge variant="success">
                          {{$t('myJobsTable.jobStatusCompletedLabel')}}
                        </b-badge>
                      </div>
                      <div v-if="data.item.status === 'Cancelled'" class="float-right">
                        <b-badge variant="danger">
                          {{$t('myJobsTable.jobStatusCancelledLabel')}}
                        </b-badge>
                      </div>
                      <div v-if="data.item.status === 'Failed'" class="float-right">
                        <b-badge variant="danger">
                          {{$t('myJobsTable.jobStatusFailedLabel')}}
                        </b-badge>
                      </div>
                    </b-col>
                  </b-row>
                </template>
              </b-table>
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col cols="12" class="my-1">
              <b-pagination
                class="float-right my-0"
                :total-rows="jobs(filter).length"
                :per-page="perPage"
                v-model="currentPage"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';

  export default {
    computed: mapGetters({
      jobs: 'jobs/filterJobs',
    }),
    data() {
      return {
        loading: true,
        jobIntervalId: null,
        perPage: 10,
        currentPage: 1,
        filter: {
          jobName: '',
          status: 'All',
          fromDate: null,
          toDate: null,
        },
        allowedStatuses: [
          {
            value: 'All',
            label: this.$t('myJobsTable.jobStatusAllLabel'),
          },
          {
            value: 'Processing',
            label: this.$t('myJobsTable.jobStatusProcessingLabel'),
          },
          {
            value: 'Completed',
            label: this.$t('myJobsTable.jobStatusCompletedLabel'),
          },
        ],
        fields: [
          {
            key: 'name',
            label: 'Job Name',
          },
          'inputLanguage',
          'desiredLanguage',
          'status',
          'actions',
        ],
        simpleFields: [
          'jobInfo',
        ],
        showSimpleTable: window.innerWidth < 768,
      };
    },
    mounted() {
      this.getJobs();
      this.pollJobs();
      window.addEventListener('resize', () => {
        const newWidth = window.innerWidth;
        this.showSimpleTable = newWidth < 768;
      });
    },
    beforeDestroy() {
      clearInterval(this.jobIntervalId);
    },
    methods: {
      pollJobs() {
        this.jobIntervalId = setTimeout(() => {
          this.getJobs();
        }, 10000);
      },
      getJobs() {
        this.$store.dispatch('jobs/getJobs')
          .then(() => {
            this.loading = false;
          })
          .catch(() => {
            this.$toasted.error(this.$t('myJobsTable.errorFetchingJobs'));
          });
      },
      viewJob(jobId) {
        this.$router.push(`/view-job/${jobId}`);
      },
    },
  };
</script>

<style scoped>

</style>
